import * as React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import InvestmentTrendChart from "../components/InvestmentTrendChart";
import ActivityHourlyAverageChart from "../components/ActivityHourlyAverageChart";
import ActivityDailyUsersChart from "../components/ActivityDailyUsersChart";

export default function DashboardPage() {
  const theme = useTheme();
  const { t } = useTranslation(["main"]);

  return (
    <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "radial-gradient(circle at top right, rgba(25, 118, 210, 0.12) 0%, rgba(25, 118, 210, 0.02) 38%, rgba(255, 255, 255, 0) 65%)",
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2.5}
          sx={{ alignItems: { xs: "flex-start", lg: "center" } }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              flexShrink: 0,
            }}
          >
            <InsightsRoundedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 1,
                maxWidth: 680,
                fontWeight: 700,
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
              }}
            >
              {t("main:dashboard.title")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: 760,
                color: "text.secondary",
                lineHeight: 1.7,
                fontSize: { xs: "1rem", md: "1.0625rem" },
              }}
            >
              {t("main:dashboard.subtitle")}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              alignSelf: { xs: "stretch", sm: "flex-start", lg: "center" },
              px: 1.5,
              py: 1,
              borderRadius: 999,
              color: "text.secondary",
              bgcolor: alpha(theme.palette.background.paper, 0.92),
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.14),
              boxShadow: `0 14px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <QueryStatsRoundedIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("main:dashboard.liveData")}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          alignItems: "stretch",
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", minHeight: { md: 560 } }}>
          <InvestmentTrendChart />
        </Box>
        <Box sx={{ minWidth: 0, display: "flex", minHeight: { md: 560 } }}>
          <ActivityHourlyAverageChart />
        </Box>
        <Box sx={{ minWidth: 0, display: "flex", minHeight: { md: 560 } }}>
          <ActivityDailyUsersChart />
        </Box>
      </Box>
    </Box>
  );
}
